import { IconRefresh } from "@tabler/icons-react";
import { useState } from "react";
import { ApiError, staffApi, type LoyaltyProgramme, type OnCallEntry, type StaffEmergency } from "../api";
import { formatDate, formatDateTime, relativeTime } from "../format";
import { useStaffData } from "./staffData";

const describe = (err: unknown) => (err instanceof ApiError ? `${err.code}${err.message && err.message !== err.code ? `: ${err.message}` : ""}` : err instanceof Error ? err.message : "failed");

const BRANDS = ["*", "azores", "portugal", "yes"];
const ALL = "*";

// ── Emergencies ───────────────────────────────────────────────────────────────

/** The Help now cases, newest first, with the trip, the agent rung and a Close with note. */
export function EmergenciesTab({ onError }: { onError: (err: unknown) => void }) {
  const { data, error, reload } = useStaffData(() => staffApi.emergencies(), onError, []);
  const cases = [...(data?.cases ?? [])].sort((a, b) => b.openedAt.localeCompare(a.openedAt));
  const open = cases.filter((c) => c.status === "open").length;
  return (
    <section className="stack">
      <div className="section-title">
        <h2>
          Emergencies ({cases.length}{open ? `, ${open} open` : ""})
        </h2>
        <button type="button" className="icon-button" onClick={reload} aria-label="Refresh">
          <IconRefresh size={18} />
        </button>
      </div>
      {error && <p className="error-note">{error}</p>}
      {data && cases.length === 0 && <p className="muted">No cases yet. A customer opens one with "Help now" on a confirmed or ongoing trip.</p>}
      <div className="stack">
        {cases.map((c) => (
          <EmergencyCard key={c.id} item={c} onChanged={reload} onError={onError} />
        ))}
      </div>
    </section>
  );
}

function EmergencyCard({ item: c, onChanged, onError }: { item: StaffEmergency; onChanged: () => void; onError: (err: unknown) => void }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = async () => {
    setBusy(true);
    setError(null);
    try {
      await staffApi.closeEmergency(c.id, { by: "staff", ...(note.trim() ? { note: note.trim() } : {}) });
      onChanged();
    } catch (err) {
      onError(err);
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <article className={`card outbox-card ${c.status === "closed" ? "read" : ""}`}>
      <div className="outbox-meta">
        <span className={`chip chip-urgency-${c.urgency}`}>{c.urgency}</span>
        <span className={`chip chip-${c.status}`}>{c.status}</span>
        {c.trip && <span className="chip">{c.trip.brand}</span>}
        <span className="muted small" title={formatDateTime(c.openedAt, "en")}>
          {relativeTime(c.openedAt, "en")}
        </span>
        {c.assignedAgentName ? (
          <span className="muted small">
            rang {c.assignedAgentName}
            {c.assignedVia ? ` (${c.assignedVia})` : ""}
          </span>
        ) : (
          <span className="muted small">nobody rung</span>
        )}
      </div>
      <p className="small">
        <strong>{c.trip ? `${c.trip.reference} · ${c.trip.title}` : c.tripId}</strong>
        {c.trip?.holderName ? ` · ${c.trip.holderName}` : ""}
      </p>
      <p className="message-body">{c.what}</p>
      <p className="muted small">
        {c.where ? `Where: ${c.where} · ` : ""}
        {c.callbackPhone ? `Call back: ${c.callbackPhone}` : "No callback number"}
      </p>
      {c.assistantReply && (
        <details className="event-card">
          <summary className="small">Assistant reply</summary>
          <p className="message-body small">{c.assistantReply}</p>
        </details>
      )}
      {c.status === "closed" ? (
        <p className="muted small">
          Closed {c.closedAt ? relativeTime(c.closedAt, "en") : ""}
          {c.note ? ` · ${c.note}` : ""}
        </p>
      ) : (
        <div className="row-buttons">
          <input className="inline-input" type="text" placeholder="Closing note (optional)" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
          <button type="button" className="button button-small button-primary" disabled={busy} onClick={() => void close()}>
            {busy ? "Closing…" : "Close"}
          </button>
        </div>
      )}
      {error && <p className="error-note small">{error}</p>}
    </article>
  );
}

// ── On call ───────────────────────────────────────────────────────────────────

const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return isoDay(d);
};

/** The on-call roster of the next 14 days (docs/16 §2.8): agent and backup per day, for all brands or one. */
export function OnCallTab({ onError }: { onError: (err: unknown) => void }) {
  const [from] = useState(() => isoDay(new Date()));
  const to = addDays(from, 13);
  const [brand, setBrand] = useState(ALL);
  const { data, error, reload } = useStaffData(() => staffApi.onCall(from, to), onError, [from, to]);
  const [busy, setBusy] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const days = Array.from({ length: 14 }, (_, i) => addDays(from, i));
  const byDay = new Map<string, OnCallEntry>();
  for (const e of data?.entries ?? []) if (e.brand === brand) byDay.set(e.day, e);
  const fallback = new Map<string, OnCallEntry>();
  for (const e of data?.entries ?? []) if (e.brand === ALL) fallback.set(e.day, e);

  const save = async (day: string, agentId: string, backupAgentId: string | null) => {
    if (!agentId) return;
    setBusy(day);
    setSaveError(null);
    try {
      await staffApi.putOnCall(day, { ...(brand === ALL ? {} : { brand }), agentId, backupAgentId });
      reload();
    } catch (err) {
      onError(err);
      setSaveError(describe(err));
    } finally {
      setBusy(null);
    }
  };

  const agents = data?.agents ?? [];
  const nameOf = (id: string | null) => (id ? (agents.find((a) => a.id === id)?.name ?? id.slice(0, 8)) : "—");

  return (
    <section className="stack">
      <div className="section-title">
        <h2>On call · next 14 days</h2>
        <div className="row-buttons">
          <select className="inline-input" value={brand} onChange={(e) => setBrand(e.target.value)} aria-label="Brand">
            {BRANDS.map((b) => (
              <option key={b} value={b}>
                {b === ALL ? "All brands" : b}
              </option>
            ))}
          </select>
          <button type="button" className="icon-button" onClick={reload} aria-label="Refresh">
            <IconRefresh size={18} />
          </button>
        </div>
      </div>
      {error && <p className="error-note">{error}</p>}
      {saveError && <p className="error-note">{saveError}</p>}
      {data && !data.crm && <p className="notice small">The CRM is not connected on this environment: the roster cannot be read or changed here.</p>}
      {data?.crm && (
        <>
          <p className="muted small">Urgent cases ring the agent of the day, then the backup after two minutes. A brand row overrides the "All brands" row for that brand.</p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Agent</th>
                  <th>Backup</th>
                  {brand !== ALL && <th>All brands</th>}
                  <th />
                </tr>
              </thead>
              <tbody>
                {days.map((day) => (
                  <OnCallRow key={`${brand}:${day}`} day={day} entry={byDay.get(day) ?? null} fallback={brand !== ALL ? (fallback.get(day) ?? null) : undefined} agents={agents} nameOf={nameOf} busy={busy === day} onSave={save} />
                ))}
              </tbody>
            </table>
          </div>
          {agents.length === 0 && <p className="muted small">The CRM returned no agents; the selects are empty.</p>}
        </>
      )}
    </section>
  );
}

function OnCallRow({ day, entry, fallback, agents, nameOf, busy, onSave }: { day: string; entry: OnCallEntry | null; fallback: OnCallEntry | null | undefined; agents: { id: string; name: string }[]; nameOf: (id: string | null) => string; busy: boolean; onSave: (day: string, agentId: string, backupAgentId: string | null) => Promise<void> }) {
  const [agentId, setAgentId] = useState(entry?.agentId ?? "");
  const [backupId, setBackupId] = useState(entry?.backupAgentId ?? "");
  const dirty = agentId !== (entry?.agentId ?? "") || backupId !== (entry?.backupAgentId ?? "");
  const today = day === new Date().toISOString().slice(0, 10);
  return (
    <tr className={today ? "day-today" : ""}>
      <td>
        {formatDate(day, "en", { weekday: "short", day: "numeric", month: "short" })}
        {today && <span className="chip chip-today">today</span>}
      </td>
      <td>
        <select className="inline-input" value={agentId} onChange={(e) => setAgentId(e.target.value)} aria-label={`Agent on ${day}`}>
          <option value="">—</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </td>
      <td>
        <select className="inline-input" value={backupId} onChange={(e) => setBackupId(e.target.value)} aria-label={`Backup on ${day}`}>
          <option value="">—</option>
          {agents
            .filter((a) => a.id !== agentId)
            .map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
        </select>
      </td>
      {fallback !== undefined && (
        <td className="muted small">
          {fallback ? `${nameOf(fallback.agentId)}${fallback.backupAgentId ? ` / ${nameOf(fallback.backupAgentId)}` : ""}` : "—"}
        </td>
      )}
      <td>
        <button type="button" className="button button-small button-primary" disabled={busy || !dirty || !agentId} onClick={() => void onSave(day, agentId, backupId || null)}>
          {busy ? "Saving…" : "Save"}
        </button>
      </td>
    </tr>
  );
}

// ── Programme (Status tab) ────────────────────────────────────────────────────

/** The loyalty programme as the CRM holds it, and the button that names it Getaways Club with its three tiers. */
export function ProgrammeBlock({ onError }: { onError: (err: unknown) => void }) {
  const { data, error, reload } = useStaffData(() => staffApi.loyaltyProgramme(), onError, []);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const programme: LoyaltyProgramme | null = data?.programme ?? null;

  const ensure = async () => {
    setBusy(true);
    setResult(null);
    try {
      const res = await staffApi.ensureProgramme();
      setResult(res.programme ? `Programme is now "${res.programme.name ?? "?"}"` : "Done (no CRM connected: nothing to apply)");
      reload();
    } catch (err) {
      onError(err);
      setResult(describe(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="card stack">
      <div className="section-title">
        <h3>Loyalty programme</h3>
        <button type="button" className="button button-small button-primary" disabled={busy} onClick={() => void ensure()}>
          {busy ? "Applying…" : "Apply Getaways Club"}
        </button>
      </div>
      {error && <p className="error-note small">{error}</p>}
      {result && <p className="small">{result}</p>}
      {data && !programme && <p className="muted small">No programme read from the CRM (not connected, or none configured yet).</p>}
      {programme && (
        <>
          <p className="small">
            <strong>{programme.name ?? "(unnamed)"}</strong>
            {programme.earnPointsPerUnit !== undefined ? ` · earn ${programme.earnPointsPerUnit} pt/unit` : ""}
            {programme.redeemPointsPerUnit !== undefined ? ` · redeem ${programme.redeemPointsPerUnit} pts/unit` : ""}
            {programme.expiryMonths !== undefined ? ` · expiry ${programme.expiryMonths} months` : ""}
          </p>
          <ul className="list">
            {(programme.tiers ?? []).map((tier) => (
              <li key={tier.code} className="list-row">
                <span className="list-text">
                  <strong>
                    {tier.name} <code className="small">{tier.code}</code>
                  </strong>
                  <span className="muted small">
                    from {tier.threshold?.default ?? 0} pts
                    {tier.benefits?.length ? ` · ${tier.benefits.join("; ")}` : ""}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </article>
  );
}
